import React, { useEffect, useState } from 'react';
import { WifiOff, CloudUpload, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { useNetworkStatus, processSyncQueue, prefetchForOffline } from '../offline/sync';
import { offlineDB } from '../offline/db';
import { pullSinceLastSync } from '../offline/repo';
import { toast } from 'sonner';

/**
 * OfflineBanner — toujours visible si offline ou si pending > 0.
 * Déclenche aussi la sync automatique au retour online.
 */
export const OfflineBanner = () => {
  const { online, pending, refreshPending } = useNetworkStatus();
  const [syncing, setSyncing] = useState(false);
  const [conflicts, setConflicts] = useState(0);

  // Pre-fetch les données offline une fois quand on est connecté
  useEffect(() => {
    if (online) {
      prefetchForOffline();
      // Pull différentiel des changements serveur depuis la dernière sync
      pullSinceLastSync().catch(() => null);
      offlineDB.conflict_log.count().then(setConflicts);
    }
  }, [online]);

  // Auto-sync à chaque transition offline→online
  useEffect(() => {
    if (online && pending > 0 && !syncing) {
      runSync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  const runSync = async () => {
    setSyncing(true);
    try {
      const result = await processSyncQueue();
      if (result.processed > 0) {
        toast.success(`Synchronisation effectuée — ${result.processed} élément(s) mis à jour`);
      } else if (result.errors > 0 && result.conflicts === 0) {
        toast.warning("Aucun élément n'a pu être synchronisé. Si ça persiste, utilisez 'Effacer la file' (clic droit / shift+clic).");
      }
      if (result.conflicts > 0) {
        toast.error(`${result.conflicts} conflit(s) détecté(s) — voir Paramètres > Conflits`);
      }
      const c = await offlineDB.conflict_log.count();
      setConflicts(c);
      refreshPending();
    } catch (e) {
      console.warn('Sync error:', e);
    } finally {
      setSyncing(false);
    }
  };

  const clearQueue = async (e) => {
    e.stopPropagation();
    if (!window.confirm("Effacer toutes les actions en attente ? (Les modifications locales non synchronisées seront perdues.)")) return;
    await offlineDB.sync_queue.clear();
    refreshPending();
    toast.success("File de synchronisation effacée");
  };

  // Aucune indication si tout est synchro et online
  if (online && pending === 0 && conflicts === 0 && !syncing) return null;

  let bgClass, IconComp, label, sub;
  if (!online) {
    bgClass = 'bg-amber-500/10 border-amber-500/30 text-amber-300';
    IconComp = WifiOff;
    label = 'Mode hors-ligne — données locales';
    sub = pending > 0 ? `${pending} action(s) en attente` : 'Vos modifications seront synchronisées au retour réseau';
  } else if (syncing) {
    bgClass = 'bg-blue-500/10 border-blue-500/30 text-blue-300';
    IconComp = Loader2;
    label = 'Synchronisation en cours...';
    sub = `${pending} élément(s) à pousser`;
  } else if (pending > 0) {
    bgClass = 'bg-blue-500/10 border-blue-500/30 text-blue-300';
    IconComp = CloudUpload;
    label = `${pending} action(s) en attente de synchronisation`;
    sub = 'Cliquez pour synchroniser maintenant';
  } else if (conflicts > 0) {
    bgClass = 'bg-red-500/10 border-red-500/30 text-red-300';
    IconComp = AlertTriangle;
    label = `${conflicts} conflit(s) à résoudre`;
    sub = 'Certaines modifications n\'ont pas pu être appliquées';
  } else {
    bgClass = 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300';
    IconComp = CheckCircle2;
    label = 'Synchronisé';
    sub = '';
  }

  return (
    <div
      className={`sticky top-0 z-50 px-3 py-1.5 border-b ${bgClass} flex items-center gap-2 text-xs cursor-pointer select-none`}
      onClick={!online ? undefined : runSync}
      data-testid="offline-banner"
    >
      <IconComp className={`w-4 h-4 flex-shrink-0 ${syncing ? 'animate-spin' : ''}`} />
      <span className="font-semibold">{label}</span>
      {sub && <span className="opacity-70 hidden sm:inline">— {sub}</span>}
      {online && pending > 0 && !syncing && (
        <button
          onClick={clearQueue}
          className="ml-auto px-2 py-0.5 rounded hover:bg-white/10 underline opacity-70"
          data-testid="offline-banner-clear"
          title="Si ces actions restent bloquées en boucle, vide la file. Tu peux les re-saisir depuis l'app."
        >
          Effacer la file
        </button>
      )}
    </div>
  );
};

export default OfflineBanner;
